import React from 'react';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import Joyride, { Styles as JoyrideStyles } from 'react-joyride';

import { runInAction } from 'mobx';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';

import i18n, { GlobalLocaleStore } from 'services/base/i18n';
import { BasePath } from 'services/base/page_path';
import { togglePullToRefresh } from 'services/base/ipc';
import { formatUnits } from 'services/base/format';

import { ResultKind } from 'services/base/types';
import { Pangolin } from 'services/accounts/types';
import { useAccountsDomain } from 'services/accounts/accounts_domain';
import { IPangolinDomain, NetworkCounters } from 'services/pangolin/types';
import { usePangolinDomain } from 'services/pangolin/pangolin_domain';
import { countConnectedDevices } from 'services/pangolin/hosts_service';
import { IssuesService } from 'services/pangolin/issues_service';

import {
  getPlanFirewallPackageName,
  getPlanFirewallPlanActivePackage,
} from 'services/accounts/logic/plans/plan_firewall';

import { Link } from 'ui/base/link';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';
import { CorePage } from 'ui/base/page/core_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';

import { PangolinSelectorModal } from 'ui/standard/pangolin_selector/pangolin_selector';
import { PangolinNetworkThroughput } from 'ui/devices/network_throughput/network_throughput';

import { Redirect } from 'pages/core/redirect/redirect';

import Messages from './home_messages';
import baseStyles from './home.module.css';

import midnightHeroImageSafeImage from './assets/safe/dark.png';
import daylightHeroImageSafeImage from './assets/safe/light.png';
import midnightHeroImageOfflineImage from './assets/offline/dark.png';
import daylightHeroImageOfflineImage from './assets/offline/light.png';
import midnightHeroImageAlertImage from './assets/alert/dark.png';
import daylightHeroImageAlertImage from './assets/alert/light.png';

import fieldIconImagePlanGoodMidnight from './assets/fields/plan_good/dark.png';
import fieldIconImagePlanGoodDaylight from './assets/fields/plan_good/light.png';
import fieldIconImagePlanWarnMidnight from './assets/fields/plan_warn/dark.png';
import fieldIconImagePlanWarnDaylight from './assets/fields/plan_warn/light.png';
import fieldIconImageUpdateGoodMidnight from './assets/fields/update_good/dark.png';
import fieldIconImageUpdateGoodDaylight from './assets/fields/update_good/light.png';
import fieldIconImageUpdateWarnMidnight from './assets/fields/update_warn/dark.png';
import fieldIconImageUpdateWarnDaylight from './assets/fields/update_warn/light.png';

import {
  getDeploymentProfileIconImage,
  getDeploymentProfileName,
  getDeploymentSettingsProfile,
  themedDeploymentProfileIconImageStyles,
} from 'assets/enums/deployment_profiles/deployment_profiles';

import moduleIconImageTrafficHistoryMidnight from 'assets/enums/modules/module_icons/history/dark.png';
import moduleIconImageTrafficHistoryDaylight from 'assets/enums/modules/module_icons/history/light.png';
import moduleIconImageBandwidthControlsMidnight from 'assets/enums/modules/module_icons/bandwidth/dark.png';
import moduleIconImageBandwidthControlsDaylight from 'assets/enums/modules/module_icons/bandwidth/light.png';
import moduleIconImageParentalControlsMidnight from 'assets/enums/modules/module_icons/schedules/dark.png';
import moduleIconImageParentalControlsDaylight from 'assets/enums/modules/module_icons/schedules/light.png';
import moduleIconImageVPNServerMidnight from 'assets/enums/modules/module_icons/vpnserver/dark.png';
import moduleIconImageVPNServerDaylight from 'assets/enums/modules/module_icons/vpnserver/light.png';

import { HomeStore } from './home_store';

type HomeStyles = {
  header: string;
  update: string;

  heroImageContainer: string;
  heroImage: string;
  heroTextContainer: string;
  heroTitle: string;
  heroText: string;
  heroLink: string;

  heroImageSafeImage: string;
  heroImageOfflineImage: string;
  heroImageAlertImage: string;

  fieldsContainer: string;
  field: string;
  fieldIcon: string;
  fieldAlert: string;
  fieldBody: string;
  fieldValue: string;
  fieldValueSmall: string;
  fieldLabel: string;

  fieldIconImagePlanGood: string;
  fieldIconImagePlanWarn: string;
  fieldIconImageUpdateGood: string;
  fieldIconImageUpdateWarn: string;

  modulesContainer: string;
  module: string;
  moduleIcon: string;
  moduleBody: string;
  moduleText: string;
  moduleTextSmall: string;
  moduleLabel: string;

  moduleIconImageTrafficHistory: string;
  moduleIconImageBandwidthControls: string;
  moduleIconImageSchedules: string;
  moduleIconImageVPNServer: string;

  divider: string;

  countersContainer: string;
  countersRow: string;
  countersColumn: string;
  countersLabel: string;
  countersData: string;

  throughputContainer: string;

  alertCritical: string;
  alertWarning: string;

  open: string;

  clickable: string;
  disabled: string;
};

const themedStyles: ThemedStyles<HomeStyles> = {
  [Theme.DAYLIGHT]: {
    header: baseStyles.baseHeader,
    update: baseStyles.baseUpdate,

    heroImageContainer: baseStyles.heroImageContainer,
    heroImage: baseStyles.heroImage,
    heroTextContainer: baseStyles.heroTextContainer,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroLink: baseStyles.daylightHeroLink,

    heroImageSafeImage: daylightHeroImageSafeImage,
    heroImageOfflineImage: daylightHeroImageOfflineImage,
    heroImageAlertImage: daylightHeroImageAlertImage,

    fieldsContainer: baseStyles.fieldsContainer,
    field: baseStyles.daylightField,
    fieldIcon: baseStyles.fieldIcon,
    fieldAlert: baseStyles.fieldAlert,
    fieldBody: baseStyles.fieldBody,
    fieldValue: baseStyles.fieldValue,
    fieldValueSmall: baseStyles.fieldValueSmall,
    fieldLabel: baseStyles.fieldLabel,

    fieldIconImagePlanGood: fieldIconImagePlanGoodDaylight,
    fieldIconImagePlanWarn: fieldIconImagePlanWarnDaylight,
    fieldIconImageUpdateGood: fieldIconImageUpdateGoodDaylight,
    fieldIconImageUpdateWarn: fieldIconImageUpdateWarnDaylight,

    modulesContainer: baseStyles.modulesContainer,
    module: baseStyles.daylightModule,
    moduleIcon: baseStyles.moduleIcon,
    moduleBody: baseStyles.moduleBody,
    moduleText: baseStyles.moduleText,
    moduleTextSmall: baseStyles.moduleTextSmall,
    moduleLabel: baseStyles.moduleLabel,

    moduleIconImageTrafficHistory: moduleIconImageTrafficHistoryDaylight,
    moduleIconImageBandwidthControls: moduleIconImageBandwidthControlsDaylight,
    moduleIconImageSchedules: moduleIconImageParentalControlsDaylight,
    moduleIconImageVPNServer: moduleIconImageVPNServerDaylight,

    divider: baseStyles.daylightDivider,

    countersContainer: baseStyles.countersContainer,
    countersRow: baseStyles.countersRow,
    countersColumn: baseStyles.countersColumn,
    countersLabel: baseStyles.countersLabel,
    countersData: baseStyles.countersData,

    throughputContainer: baseStyles.daylightThroughputContainer,

    alertCritical: baseStyles.alertCritical,
    alertWarning: baseStyles.alertWarning,

    open: baseStyles.daylightOpen,

    clickable: baseStyles.clickable,
    disabled: baseStyles.disabled,
  },
  [Theme.MIDNIGHT]: {
    header: baseStyles.baseHeader,
    update: baseStyles.baseUpdate,

    heroImageContainer: baseStyles.heroImageContainer,
    heroImage: baseStyles.heroImage,
    heroTextContainer: baseStyles.heroTextContainer,
    heroTitle: baseStyles.heroTitle,
    heroText: baseStyles.heroText,
    heroLink: baseStyles.midnightHeroLink,

    heroImageSafeImage: midnightHeroImageSafeImage,
    heroImageOfflineImage: midnightHeroImageOfflineImage,
    heroImageAlertImage: midnightHeroImageAlertImage,

    fieldsContainer: baseStyles.fieldsContainer,
    field: baseStyles.midnightField,
    fieldIcon: baseStyles.fieldIcon,
    fieldAlert: baseStyles.fieldAlert,
    fieldBody: baseStyles.fieldBody,
    fieldValue: baseStyles.fieldValue,
    fieldValueSmall: baseStyles.fieldValueSmall,
    fieldLabel: baseStyles.fieldLabel,

    fieldIconImagePlanGood: fieldIconImagePlanGoodMidnight,
    fieldIconImagePlanWarn: fieldIconImagePlanWarnMidnight,
    fieldIconImageUpdateGood: fieldIconImageUpdateGoodMidnight,
    fieldIconImageUpdateWarn: fieldIconImageUpdateWarnMidnight,

    modulesContainer: baseStyles.modulesContainer,
    module: baseStyles.midnightModule,
    moduleIcon: baseStyles.moduleIcon,
    moduleBody: baseStyles.moduleBody,
    moduleText: baseStyles.moduleText,
    moduleTextSmall: baseStyles.moduleTextSmall,
    moduleLabel: baseStyles.moduleLabel,

    moduleIconImageTrafficHistory: moduleIconImageTrafficHistoryMidnight,
    moduleIconImageBandwidthControls: moduleIconImageBandwidthControlsMidnight,
    moduleIconImageSchedules: moduleIconImageParentalControlsMidnight,
    moduleIconImageVPNServer: moduleIconImageVPNServerMidnight,

    divider: baseStyles.midnightDivider,

    countersContainer: baseStyles.countersContainer,
    countersRow: baseStyles.countersRow,
    countersColumn: baseStyles.countersColumn,
    countersLabel: baseStyles.countersLabel,
    countersData: baseStyles.countersData,

    throughputContainer: baseStyles.midnightThroughputContainer,

    alertCritical: baseStyles.alertCritical,
    alertWarning: baseStyles.alertWarning,

    open: baseStyles.midnightOpen,

    clickable: baseStyles.clickable,
    disabled: baseStyles.disabled,
  },
};

const themedJoyrideStyles: ThemedStyles<JoyrideStyles> = {
  [Theme.DAYLIGHT]: {
    options: {
      arrowColor: '#ffc107',
      backgroundColor: '#ffc107',
      textColor: '#000',
      width: 280,
    },
  },
  [Theme.MIDNIGHT]: {
    options: {
      arrowColor: '#2d539d',
      backgroundColor: '#2d539d',
      textColor: '#fff',
      width: 280,
    },
  },
};

export const OfflineHero = observer(() => {
  const styles = useStyles(themedStyles);
  return (
    <>
      <div className={styles.heroImageContainer}>
        <img src={styles.heroImageOfflineImage} className={styles.heroImage} alt='safe' />
      </div>
      <div className={styles.heroTextContainer}>
        <div className={styles.heroTitle}>{Messages.offlineTitle()}</div>
        <div className={styles.heroText}>{Messages.offlineText()}</div>
      </div>
    </>
  );
});


export const SafeHero = observer(() => {
  const styles = useStyles(themedStyles);
  return (
    <>
      <div className={styles.heroImageContainer}>
        <img src={styles.heroImageSafeImage} className={styles.heroImage} alt='safe' />
      </div>
      <div className={styles.heroTextContainer}>
        <div className={styles.heroTitle}>{Messages.safeTitle()}</div>
        <div className={styles.heroText}>{Messages.safeText()}</div>
      </div>
    </>
  );
});

type ThreatsHeroProps = {
  pangolin: Pangolin;
  threats: number;
};

export const ThreatsHero = observer(({ pangolin, threats }: ThreatsHeroProps) => {
  const styles = useStyles(themedStyles);
  return (
    <>
      <div className={styles.heroImageContainer}>
        <img src={styles.heroImageAlertImage} className={styles.heroImage} alt='safe' />
      </div>
      <div className={styles.heroTextContainer}>
        <div className={styles.heroTitle}>{Messages.alertsText(threats)}</div>
        <Link className={styles.heroLink} replace
              path={{ kind: BasePath.LIST_ISSUES,
                      pangolinID: pangolin.id }}>
          {Messages.viewIssues()}
        </Link>
      </div>
    </>
  );
});

type NetworkCountersDisplayProps = {
  domain: IPangolinDomain;
  counters: NetworkCounters;
};

export const NetworkCountersDisplay = observer(({ domain, counters }: NetworkCountersDisplayProps) => {
  const styles = useStyles(themedStyles);

  const trackersBlocked = counters.adblockBlocked;
  const threatsBlocked = counters.malwareBlocked + counters.maliciousBlocked;
  const totalQueries = counters.filtersTotal + counters.adblockTotal + counters.maliciousTotal + counters.malwareTotal;
  const blockedQueries = counters.filtersBlocked + counters.adblockBlocked + counters.maliciousBlocked + counters.malwareBlocked;
  const percentBlocked = blockedQueries / totalQueries;

  return (
    <>
      <div className={styles.countersContainer}>
        <div className={styles.countersRow}>
          <div className={styles.countersColumn}>
            <div className={styles.countersData}>
              {formatUnits(countConnectedDevices(domain.hosts))}
            </div>
            <div className={styles.countersLabel}>
              {Messages.devicesOnline()}
            </div>
          </div>
          <div className={styles.countersColumn}>
            <div className={styles.countersData}>
              {formatUnits(trackersBlocked)}
            </div>
            <div className={styles.countersLabel}>
              {Messages.trackersBlocked()}
            </div>
          </div>
          <div className={styles.countersColumn}>
            <div className={styles.countersData}>
              {formatUnits(threatsBlocked)}
            </div>
            <div className={styles.countersLabel}>
              {Messages.threatsBlocked()}
            </div>
          </div>
        </div>

        <div className={styles.countersRow}>
          <div className={styles.countersColumn}>
            <div className={styles.countersData}>
              {formatUnits(totalQueries)}
            </div>
            <div className={styles.countersLabel}>
              {Messages.totalQueries()}
            </div>
          </div>
          <div className={styles.countersColumn}>
            <div className={styles.countersData}>
              {formatUnits(blockedQueries)}
            </div>
            <div className={styles.countersLabel}>
              {Messages.blockedQueries()}
            </div>
          </div>
          <div className={styles.countersColumn}>
            <div className={styles.countersData}>
              {i18n.percentFormat(percentBlocked, 1)}
            </div>
            <div className={styles.countersLabel}>
              {Messages.percentBlockedQueries()}
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

type NetworkThroughputDisplayProps = {
  domain: IPangolinDomain;
};

export const HomeThroughputDisplay = observer(({ domain }: NetworkThroughputDisplayProps) => {
  const styles = useStyles(themedStyles);
  return (
    <div className={styles.throughputContainer}>
      <PangolinNetworkThroughput pangolin={domain}/>
    </div>
  );
});

type BaseHomeProps = {
  pangolin: Pangolin;
  domain: IPangolinDomain;
};
export const BaseHome = observer(({ pangolin, domain }: BaseHomeProps) => {
  const styles = useStyles(themedStyles);
  const joyride = useStyles(themedJoyrideStyles);

  const locale = GlobalLocaleStore.get();

  const [threats, setThreats] = React.useState(0);

  React.useEffect(() => {
    let mounted = true;

    runInAction(async() => {
      if (mounted && pangolin.onlineStatus) {
        const result = await IssuesService.fetchPage(domain, pangolin, false);
        if (mounted && result.kind === ResultKind.OK) {
          setThreats(result.issues.filter(i => (
            i.incidents.some(ii => !ii.seen)
          )).size);
        }
      }
    });

    return () => {
      mounted = false;
    };
  });

  const [store] = React.useState(new HomeStore());

  const onScroll = React.useCallback((event: React.UIEvent<HTMLDivElement>) => {
    togglePullToRefresh(event.currentTarget.scrollTop === 0);
  }, []);

  React.useEffect(() => {
    togglePullToRefresh(true);
    return () => {
      togglePullToRefresh(false);
    };
  }, []);

  const profile = getDeploymentSettingsProfile({
    enablePhysicalIntercept: pangolin.redirectModeBridgeEnabled,
    enableUnicastSpoofing: pangolin.redirectModeUnicastEnabled,
    useUnicastRequest: pangolin.redirectModeUnicastTypeRequest,
    useUnicastResponse: pangolin.redirectModeUnicastTypeResponse,
    redirectInternetWithUnicast: pangolin.redirectModeUnicastNetworkInternet,
    redirectInternalWithUnicast: pangolin.redirectModeUnicastNetworkInternal,
    enableBroadcastSpoofing: pangolin.redirectModeBroadcastEnabled,
    useBroadcastRequest: pangolin.redirectModeBroadcastTypeRequest,
    useBroadcastResponse: pangolin.redirectModeBroadcastTypeResponse,
    spoofGatewayWithBroadcast: pangolin.redirectModeBroadcastHostGateways,
    spoofHostsWithBroadcast: pangolin.redirectModeBroadcastHostHosts,
    masqueradeEgress: pangolin.masqueradeEgress,
  });

  const profileIconImages = useStyles(themedDeploymentProfileIconImageStyles);
  const profileIcon = getDeploymentProfileIconImage(profileIconImages, profile);
  const profileName = getDeploymentProfileName(profile);
  const profileNameShort = profileName.split(' ', 1)[0];

  const plan = pangolin.plans.firewall;

  const planActivePackage = getPlanFirewallPlanActivePackage(plan);
  const planActivePackageName = planActivePackage ? getPlanFirewallPackageName(planActivePackage) : '';

  const planActive = !!planActivePackage;
  const planExpired = !planActive && (plan?.license || plan?.subscription);

  const updateLatestRelease = (
    !domain.updateReleases.size ? undefined :
    domain.updateReleases.getAt(0)
  );
  const updateCurrentRelease = (
    !pangolin.updateActiveReleaseID ? undefined :
    domain.updateReleases.get(pangolin.updateActiveReleaseID)
  );

  const updateLatestTag = updateLatestRelease?.tag ?? null;
  const updateCurrentTag = updateCurrentRelease?.tag ?? pangolin.updateVersion ?? null;

  const updateUpgrade = !!pangolin.updateActiveUpgradeID;
  const updateRestore = !!pangolin.updateActiveRestoreID;

  const updateLatest = updateLatestTag === updateCurrentTag;

  const alertPlan = !planActive;
  const alertUpdate = !updateLatest;

  return (
    <CorePage pangolin={pangolin} location={BasePath.HOME} onScroll={onScroll}>
      {!planActive && (
        <Joyride steps={[{
          target: '#home-field-plan',
          content: (
            <ReactMarkdown>
              {Messages.noPlanMessage()}
            </ReactMarkdown>
          ),
          disableBeacon: true,
          disableScrolling: true,
          disableScrollParentFix: true,
          hideCloseButton: true,
          hideBackButton: true,
          hideFooter: true,
          placement: 'top-start',
          floaterProps: { disableAnimation: true },
          styles: joyride,
        }]} />
      )}
      <PangolinSelectorModal
        pangolin={pangolin}
        location={BasePath.HOME}
        store={store.modal}
      />
      {
        !pangolin.onlineStatus ? <OfflineHero/> : threats === 0 ? <SafeHero/> :
        <ThreatsHero pangolin={pangolin} threats={threats}/>
      }
      <div className={styles.fieldsContainer}>
        <div className={classNames(styles.field, styles.clickable)} onClick={store.modal.showModal} id='home-field-pangolin'>
          <ShieldIcon
            className={styles.fieldIcon}
            colorCode={pangolin.colorCode}
            size={ShieldSize.TINY}
          />
          <div className={styles.fieldBody}>
            <div className={locale === 'ja_JP' ? styles.fieldValueSmall : styles.fieldValue}>
              {pangolin.nickname}
            </div>
            <div className={styles.fieldLabel}>
              {Messages.modulePangolin()}
            </div>
          </div>
          <div className={styles.open}/>
        </div>
        <Link className={styles.field} path={{ kind: BasePath.MANAGE_PANGOLIN_INTERCEPT, pangolinID: domain.id }} id='home-field-deployment'>
          <img className={styles.fieldIcon}
               src={profileIcon}
               alt={Messages.moduleDeployment()} />
          <div className={styles.fieldBody}>
            <div className={locale === 'ja_JP' ? styles.fieldValueSmall : styles.fieldValue}>
              {profileNameShort}
            </div>
            <div className={styles.fieldLabel}>
              {Messages.moduleDeployment()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
        <Link className={styles.field} path={{ kind: BasePath.PLANS, pangolinID: domain.id }} id='home-field-plan'>
          <div className={styles.fieldAlert}>
            {alertPlan  && <div className={styles.alertCritical} />}
            <img className={styles.fieldIcon}
                 src={planActive ? styles.fieldIconImagePlanGood :
                                   styles.fieldIconImagePlanWarn}
                 alt={Messages.modulePlan()} />
          </div>
          <div className={styles.fieldBody}>
            <div className={locale === 'ja_JP' ? styles.fieldValueSmall : styles.fieldValue}>
              {planActive ? planActivePackageName : planExpired ? Messages.planExpired() : Messages.planInactive()}
            </div>
            <div className={styles.fieldLabel}>
              {Messages.modulePlan()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
        <Link className={styles.field} path={{ kind: BasePath.UPDATES, pangolinID: domain.id }} id='home-field-updates'>
          <div className={styles.fieldAlert}>
            {alertUpdate  && <div className={styles.alertCritical} />}
            <img className={styles.fieldIcon}
                 src={updateLatest ? styles.fieldIconImageUpdateGood :
                                     styles.fieldIconImageUpdateWarn}
                 alt={Messages.moduleUpdate()} />
          </div>
          <div className={styles.fieldBody}>
            <div className={locale === 'ja_JP' ? styles.fieldValueSmall : styles.fieldValue}>
              {updateUpgrade ? Messages.updateUpgrade() :
               updateRestore ? Messages.updateRestore() :
               updateLatest ? Messages.updateLatest() :
               Messages.updateAvailable()}
            </div>
            <div className={styles.fieldLabel}>
              {Messages.moduleUpdate()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
      </div>
      <div className={styles.divider}/>
      <div className={styles.modulesContainer}>
        <Link className={styles.module} path={{ kind: BasePath.HISTORY_LIST, pangolinID: domain.id }}>
          <img className={styles.moduleIcon} src={styles.moduleIconImageTrafficHistory} alt={Messages.moduleTrafficHistory()}/>
          <div className={styles.moduleBody}>
            <div className={locale === 'ja_JP' ? styles.moduleTextSmall : styles.moduleText}>
              {Messages.moduleTrafficHistory()}
            </div>
            <div className={styles.moduleLabel}>
              {Messages.moduleTrafficHistoryLabel()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
        <Link className={styles.module} path={{ kind: BasePath.BANDWIDTH_LIST, pangolinID: domain.id }}>
          <img className={styles.moduleIcon} src={styles.moduleIconImageBandwidthControls} alt={Messages.moduleBandwdithControls()}/>
          <div className={styles.moduleBody}>
            <div className={locale === 'ja_JP' ? styles.moduleTextSmall : styles.moduleText}>
              {Messages.moduleBandwdithControls()}
            </div>
            <div className={styles.moduleLabel}>
              {Messages.moduleBandwdithControlsLabel()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
        <Link className={styles.module} path={{ kind: BasePath.SCHEDULE_LIST, pangolinID: domain.id }}>
          <img className={styles.moduleIcon} src={styles.moduleIconImageSchedules} alt={Messages.moduleSchedules()}/>
          <div className={styles.moduleBody}>
            <div className={locale === 'ja_JP' ? styles.moduleTextSmall : styles.moduleText}>
              {Messages.moduleSchedules()}
            </div>
            <div className={styles.moduleLabel}>
              {Messages.moduleSchedulesLabel()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
        <Link className={styles.module} path={{ kind: BasePath.VPN_LIST, pangolinID: domain.id }}>
          <img className={styles.moduleIcon} src={styles.moduleIconImageVPNServer} alt={Messages.moduleVPNServer()}/>
          <div className={styles.moduleBody}>
            <div className={locale === 'ja_JP' ? styles.moduleTextSmall : styles.moduleText}>
              {Messages.moduleVPNServer()}
            </div>
            <div className={styles.moduleLabel}>
              {Messages.moduleVPNServerLabel()}
            </div>
          </div>
          <div className={styles.open}/>
        </Link>
      </div>
      {
        domain.counters != null && (<>
          <div className={styles.divider}/>
          <NetworkCountersDisplay
            domain={domain}
            counters={domain.counters}
          />
          <HomeThroughputDisplay
            domain={domain}
          />
        </>)
      }
    </CorePage>
  );
});

export const Home = observer(() => {
  const { pangolinID } = useParams<{ pangolinID: string }>();
  if (!pangolinID) return <Redirect />;

  const accounts = useAccountsDomain();

  const pangolin = accounts.pangolins.get(pangolinID);
  if (!pangolin) return <Redirect />;

  const domain = usePangolinDomain(pangolin.id);
  if (!domain) return <Redirect />;

  return (
    <BaseHome
      pangolin={pangolin}
      domain={domain}
    />
  );
});
